import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';
import ContactForm from '../components/ContactForm';
import DenmarkMap from '../components/DenmarkMap';

export default function Contact() {
  const { siteSettings, getPage } = useCms();
  const location = useLocation();
  const defaultMessage = location.state?.defaultMessage || '';

  const pageData = getPage('kontakt');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Laserrensning Pris – Få et uforpligtende tilbud her | ECOLASER</title>
        <meta name="description" content="Hvad koster laserrensning af facade, rust eller murværk? Kontakt ECOLASER for professionel rådgivning og et uforpligtende tilbud på dit projekt i dag." />
        <meta property="og:title" content="Laserrensning Pris – Få et uforpligtende tilbud her | ECOLASER" />
        <meta property="og:description" content="Hvad koster laserrensning af facade, rust eller murværk? Kontakt ECOLASER for professionel rådgivning og et uforpligtende tilbud på dit projekt i dag." />
        <meta property="og:image" content={Array.isArray(pageData?.images) && pageData.images.length > 0 ? pageData.images[0] : "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/sort-hvid.jpg"} />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Kontakt</h1>
          <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
            Har du spørgsmål om laserrensning eller ønsker du et uforpligtende tilbud på dit projekt? Vi står altid klar til at hjælpe dig videre.
          </p>
        </div>
      </section>

      {/* Contact Content Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info & Map */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-zinc-900">Virksomhedsoplysninger</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center text-white shrink-0">
                      <i className="fa-solid fa-building" />
                    </div>
                    <div>
                      <h4 className="text-zinc-900 font-bold text-sm uppercase tracking-wider">Firma</h4>
                      <p className="text-zinc-700 text-sm mt-0.5">{siteSettings.company_name}</p>
                      <p className="text-zinc-500 text-xs mt-0.5">CVR: {siteSettings.cvr}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center text-white shrink-0">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div>
                      <h4 className="text-zinc-900 font-bold text-sm uppercase tracking-wider">Adresse</h4>
                      <p className="text-zinc-700 text-sm mt-0.5">{siteSettings.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center text-white shrink-0">
                      <i className="fa-solid fa-phone" />
                    </div>
                    <div>
                      <h4 className="text-zinc-900 font-bold text-sm uppercase tracking-wider">Telefon</h4>
                      <a
                        href={siteSettings.phoneTel}
                        className="text-brand-green hover:text-brand-green-dark font-bold text-sm mt-0.5 inline-block transition-colors"
                      >
                        {siteSettings.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center text-white shrink-0">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div>
                      <h4 className="text-zinc-900 font-bold text-sm uppercase tracking-wider">E-mail</h4>
                      <a
                        href={siteSettings.emailMailto}
                        className="text-brand-green hover:text-brand-green-dark font-bold text-sm mt-0.5 inline-block transition-colors"
                      >
                        {siteSettings.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-zinc-200" />

                <div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-4">Åbningstider</h3>
                  <div className="space-y-2">
                    {siteSettings.hours.map((hour, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-zinc-500">{hour.days}</span>
                        <span className="text-zinc-800 font-semibold">{hour.time}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-zinc-200" />

                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-4 flex items-start gap-3">
                  <i className="fa-solid fa-truck-fast text-zinc-900 mt-0.5 shrink-0" />
                  <p className="text-zinc-600 text-xs leading-relaxed">
                    <strong>Landsdækkende service:</strong> Vi har base på Djursland, men kører ud til opgaver i hele Danmark – herunder Jylland, Fyn og Sjælland.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 shadow-sm space-y-6">
                <div className="space-y-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-zinc-900">Send en besked</h2>
                  <p className="text-zinc-600 text-sm">
                    Udfyld formularen herunder for at indhente et gratis og uforpligtende tilbud. Beskriv gerne din opgave og omfanget så detaljeret som muligt.
                  </p>
                </div>

                <ContactForm defaultMessage={defaultMessage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
